import {
	formatVettingId,
	parseTimestamp,
	queryCategoryLabel,
	WORK_STATUSES,
	type WorkStatus,
	workStatusLabel,
} from "@anthra/platform/domain"
import { Button } from "@anthra/ui/button"
import { cn } from "@anthra/ui/cn"
import { ErrorState } from "@anthra/ui/feedback"
import { FilterBar, FilterBarSummary } from "@anthra/ui/filter-bar"
import { Pagination } from "@anthra/ui/pagination"
import { RecordList } from "@anthra/ui/record-list"
import { Skeleton } from "@anthra/ui/skeleton"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	TableScroller,
} from "@anthra/ui/table"
import { ToggleGroup, ToggleGroupItem } from "@anthra/ui/toggle-group"
import { PageTitle } from "@anthra/ui/typography"
import { useIsTabletUp } from "@anthra/ui/use-media-query"
import { createFileRoute, Link, useRouter } from "@tanstack/react-router"
import { useId } from "react"

import { StackedInstant } from "@/components/stacked-instant"
import { UserWithOwnNumber } from "@/components/user-with-own-number"
import { copy } from "@/copy/expert-vetting-queue"
import { ItemBadge } from "@/features/activity/item-badge"
import {
	useExpertVettingRequests,
	useVettingStatusCounts,
} from "@/features/vetting/use-expert-vetting"
import { useVettingFiltersStore } from "@/stores/use-vetting-filters-store"

/**
 * The Vetting tab (US-01-004.1/.2/.3).
 *
 * A STATUS FILTER AND NOTHING ELSE. No free-text search, no category filter, no
 * date range and NO time-elapsed or SLA column — the submission date is the only
 * time information shown, and the absence is specified rather than pending
 * (US-01-004.3).
 *
 * Every request appears regardless of status, oldest submission first by default
 * (US-01-004.1).
 */
export const Route = createFileRoute("/_panel/vetting/")({
	component: VettingTab,
})

function VettingTab() {
	const router = useRouter()
	const filters = useVettingFiltersStore(state => state.filters)
	const page = useVettingFiltersStore(state => state.page)
	const setStatus = useVettingFiltersStore(state => state.setStatus)
	const setPage = useVettingFiltersStore(state => state.setPage)
	const clearFilters = useVettingFiltersStore(state => state.clearFilters)

	const requests = useExpertVettingRequests(filters, page)
	const rows = requests.data?.data ?? []
	const isTabletUp = useIsTabletUp()
	const counts = useVettingStatusCounts()
	const statusLabelId = useId()

	return (
		// THE SCREEN HAD NO LAYOUT AT ALL. The root carried `data-screen` and
		// nothing else: the stack spacing, the page padding and the rhythm
		// between the heading, the filter and the table were all rules in
		// expert-vetting-queue.css, which this rebuild deleted. Every block on
		// this screen sat flush against the next, which is what "cramping for
		// space" looks like from outside.
		<div className="flex flex-col gap-6">
			{/* VISUALLY HIDDEN UNTIL IT IS FOCUSED, which is the whole idea of a
			    skip link. It was carrying `bg-skeleton-base h-4 w-full
			    animate-pulse rounded` — the loading-skeleton classes, applied to
			    an anchor by a bulk replacement — so it rendered as a pulsing grey
			    bar across the top of the page with "Skip to the vetting queue"
			    inside it. */}
			<a
				className="focus:rounded-control focus:bg-card focus:text-foreground focus:ring-ring sr-only focus:not-sr-only focus:px-3 focus:py-2 focus:ring-2"
				href="#queue-heading"
			>
				Skip to the vetting queue
			</a>

			<div className="flex flex-col gap-1">
				<PageTitle id="queue-heading">{copy.default.pageHead}</PageTitle>
				<p className="text-description text-subtle-foreground max-w-prose">
					{copy.default.filter}
				</p>
			</div>

			<FilterBar>
				<div className="flex min-w-0 flex-col gap-2">
					<span
						className="text-field-label text-foreground"
						id={statusLabelId}
					>
						{copy.default.chips}
					</span>
					{/* THE KIT'S TOGGLE GROUP. These were hand-rolled `rounded-pill …
					    px-2.5 py-1` buttons at 27px, where every other chip row in the
					    panel is 36px — and `aria-current` on five buttons announces
					    five independent controls rather than one choice of five, which
					    is the reason ToggleGroup exists.

					    A `<fieldset>`/`<legend>` wrapped this until now, which was
					    both redundant — Radix already renders `role="radiogroup"`,
					    and a radiogroup takes its own name — and a layout hazard,
					    since a legend is not a flex item like any other. */}
					<ToggleGroup
						type="single"
						className="flex-wrap"
						value={filters.status ?? "all"}
						aria-labelledby={statusLabelId}
						onValueChange={next => {
							if (!next) return
							setStatus(next === "all" ? undefined : (next as WorkStatus))
						}}
					>
						<ToggleGroupItem value="all">
							All requests
							<span className="text-meta opacity-70">{counts.all.data ?? "—"}</span>
						</ToggleGroupItem>
						{WORK_STATUSES.map(status => (
							<ToggleGroupItem
								key={status}
								value={status}
							>
								{/* The status dot. `chip__dot--open` and friends were rules
								    in expert-vetting-queue.css, which this rebuild deleted,
								    so the dot had no size and no colour at all. */}
								<span
									className={cn(
										"rounded-circle size-2 shrink-0",
										status === "open"
											? "bg-status-open-fg"
											: status === "in_progress"
												? "bg-status-in-progress-fg"
												: status === "answered"
													? "bg-status-answered-fg"
													: "bg-status-closed-fg",
									)}
									aria-hidden="true"
								/>
								{workStatusLabel(status)}
								<span className="text-meta opacity-70">
									{counts[status].data ?? "—"}
								</span>
							</ToggleGroupItem>
						))}
					</ToggleGroup>
				</div>

				{/* The count and the way to undo the filter, divided from the
				    control that produced them. "Clear filter" was a bare <button>
				    butted straight onto the end of the sentence with no separator
				    and no button styling, so the line read "…filtered to
				    OpenClear filter". */}
				{requests.data ? (
					<FilterBarSummary>
						<p className="text-meta text-muted">
							Showing {rows.length} of{" "}
							<strong className="text-foreground">
								{requests.data.pagination.totalCount}
							</strong>{" "}
							{requests.data.pagination.totalCount === 1 ? "request" : "requests"}
							{filters.status
								? `, filtered to ${workStatusLabel(filters.status)}`
								: ""}
						</p>
						{filters.status ? (
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="ms-auto"
								onClick={clearFilters}
							>
								Clear filter
							</Button>
						) : null}
					</FilterBarSummary>
				) : null}
			</FilterBar>

			{/* A TABLE-SHAPED SKELETON. Every element in this block — the
			    container, the header strip, each row and each cell — carried the
			    identical `bg-skeleton-base h-4 w-full animate-pulse rounded`,
			    nested three deep. A 16px bar containing six more 16px bars
			    containing nothing renders as one clipped grey sliver, so the
			    loading state told the reader neither that a table was coming nor
			    how much of it. */}
			{requests.isPending ? (
				<div
					className="rounded-card border-border bg-card flex w-full flex-col gap-3 border p-4"
					role="status"
					aria-label="Loading vetting requests"
					aria-busy="true"
				>
					{[0, 1, 2, 3, 4, 5].map(row => (
						<div
							className="flex items-center gap-3"
							key={row}
							aria-hidden="true"
						>
							<Skeleton className="h-4 w-16 shrink-0" />
							<Skeleton className="h-4 flex-1" />
							<Skeleton className="h-4 w-28 shrink-0" />
							<Skeleton className="h-4 w-24 shrink-0" />
							<Skeleton className="rounded-pill h-6 w-20 shrink-0" />
						</div>
					))}
				</div>
			) : null}

			{/* ONE ERROR CARD, NOT A CARD AROUND A CARD. `ErrorState` already
			    draws a bordered, tinted alert with its own title and retry, and
			    it was being nested inside a second bordered card carrying its own
			    heading mark and its own heading — so a failed fetch rendered two
			    boxes, two borders and the word "error" twice. */}
			{requests.isError ? (
				<div className="flex flex-col gap-3">
					<ErrorState
						error={requests.error}
						onRetry={() => {
							void requests.refetch()
						}}
					/>
					<p className="text-description text-subtle-foreground max-w-prose">
						{copy.error.panelHint2}
					</p>
				</div>
			) : null}

			{requests.data && rows.length === 0 ? (
				<div className="rounded-card border-border bg-surface-muted flex flex-col items-center gap-3 border border-dashed p-10 text-center">
					{/* Accented only on the true first-run case (no status filter, so
					    zero rows means zero requests exist at all) — the design keeps
					    the filtered-empty icon neutral, since "nothing waiting HERE" is
					    a routine state and not, itself, good or bad news. */}
					<p
						className={cn(
							// `font-family-ui`: the role is sizing a circle, not setting
							// a heading, and it now carries the prose face. See kit.css.
							"text-section-title font-family-ui rounded-circle flex size-10 shrink-0 items-center justify-center border",
							filters.status
								? "border-border bg-subtle text-muted"
								: "border-primary/30 bg-accent text-primary",
						)}
						aria-hidden="true"
					>
						—
					</p>
					<h2 className="text-card-title text-foreground">
						{filters.status
							? `Nothing is waiting in ${workStatusLabel(filters.status)}`
							: copy.empty.panel}
					</h2>
					<p className="text-description text-subtle-foreground max-w-prose">
						{filters.status === "open"
							? // The design's own wording for this exact case — nothing Open
								// left because it has all been picked up or answered — with
								// the sample's "72" replaced by the real count still sitting
								// under the other statuses (copy/expert-vetting-queue.ts's
								// `panelActions2`).
								`Every request has been picked up or already answered. The other ${counts.all.data ?? 0} requests are still here under the other statuses.`
							: filters.status
								? "Every request in this status has moved on. The others are still here under the other statuses."
								: copy.empty.panelActions}
					</p>
					{filters.status ? (
						<Button
							variant="secondary"
							size="sm"
							onClick={clearFilters}
						>
							Show all requests
						</Button>
					) : (
						<p className="text-meta text-muted max-w-prose">{copy.empty.panelHint2}</p>
					)}
				</div>
			) : null}

			{/* STACKED RECORDS BELOW THE TABLET WIDTH. This table is 649px wide
			    and the phone gives it 326, so 323px of every row — the user, the
			    category, the submitted date and the status — sat off the right
			    edge with `scrollbar-hide` removing the only cue that they were
			    there. tokens.css §7 has asked for this since the first design
			    run: "tables become stacked records" at the handset width. */}
			{rows.length > 0 && !isTabletUp ? (
				<RecordList
					items={rows}
					label="Vetting requests"
					getKey={row => row.id}
					renderTitle={row => (
						<span className="flex min-w-0 items-center">
							<span
								className="max-w-full truncate font-bold"
								title={row.title}
							>
								{row.title}
							</span>
							<ItemBadge count={row.unseenCount} />
						</span>
					)}
					onItemClick={row => {
						void router.navigate({
							to: "/vetting/$vettingId",
							params: { vettingId: String(row.sequence) },
						})
					}}
					fields={[
						{
							label: copy.default.queue,
							render: row => (
								<span className="tabular-nums">
									{formatVettingId(row.sequence)}
								</span>
							),
						},
						{
							label: copy.default.queue3,
							render: row => (
								<UserWithOwnNumber
									name={row.userName}
									userReference={row.userReference}
								/>
							),
						},
						{
							label: copy.default.queue4,
							render: row => queryCategoryLabel(row.category),
						},
						{
							label: copy.default.sortLink,
							render: row => (
								<StackedInstant instant={parseTimestamp(row.submittedAt)} />
							),
						},
						{
							label: copy.default.cellId,
							render: row => workStatusLabel(row.status),
						},
					]}
				/>
			) : null}

			{rows.length > 0 && isTabletUp ? (
				<TableScroller aria-label="Vetting requests">
					<Table>
						<caption className="sr-only">
							Vetting requests, oldest submission first
						</caption>
						<TableHeader>
							<TableRow>
								{/* ONE ROLE FOR ALL SIX. They carried five different
								    ones — meta, card-title, description, meta, none —
								    so the header row read as a row of unrelated
								    fragments and the "Title" header outweighed the
								    titles beneath it. TableHead is the overline
								    treatment: small, tracked, uppercase, muted. */}
								{/* Same sizing rule as the queries dashboard: the five
								    short columns take their own min-content and the
								    title takes everything left over. */}
								<TableHead col="fit">{copy.default.queue}</TableHead>
								<TableHead col="grow">{copy.default.queue2}</TableHead>
								<TableHead col="fit">{copy.default.queue3}</TableHead>
								<TableHead col="fit">{copy.default.queue4}</TableHead>
								<TableHead col="fit">{copy.default.sortLink}</TableHead>
								<TableHead col="fit">{copy.default.cellId}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.map(row => {
								const submitted = parseTimestamp(row.submittedAt)
								return (
									<TableRow
										key={row.id}
										className="relative cursor-pointer"
									>
										<TableCell className="text-meta text-muted whitespace-nowrap tabular-nums">
											<Link
												className="focus-visible:ring-ring absolute inset-0 z-10 rounded-[inherit] focus-visible:ring-2 focus-visible:outline-none"
												to="/vetting/$vettingId"
												params={{ vettingId: String(row.sequence) }}
												aria-label={`Open vetting request ${formatVettingId(row.sequence)}: ${row.title}`}
											/>
											{formatVettingId(row.sequence)}
										</TableCell>
										{/* The per-cell "Title and reference" / "User" /
										    "Category" / "Submitted" labels that stood in
										    each cell are gone. They belonged to a CSS card
										    reflow in expert-vetting-queue.css, which hid
										    them at desktop widths and showed them when the
										    table became a list. That stylesheet was
										    deleted, so they had been rendering as visible
										    text in every row, on every width, beside the
										    column header that already says the same word. */}
										<TableCell className="max-w-[22ch]">
											<span className="flex min-w-0 items-center">
												<span
													className={cn(
														"text-card-title text-foreground truncate",
														row.hasUnseen && "font-semibold",
													)}
												>
													{row.title}
												</span>
												<ItemBadge count={row.unseenCount} />
											</span>
										</TableCell>
										<TableCell className="text-description text-foreground whitespace-nowrap">
											<UserWithOwnNumber
												name={row.userName}
												userReference={row.userReference}
											/>
										</TableCell>
										<TableCell className="text-description text-subtle-foreground whitespace-nowrap">
											{queryCategoryLabel(row.category)}
										</TableCell>
										<TableCell className="text-meta text-muted whitespace-nowrap">
											<StackedInstant instant={submitted} />
										</TableCell>
										<TableCell className="text-description text-foreground">
											{workStatusLabel(row.status)}
										</TableCell>
									</TableRow>
								)
							})}
						</TableBody>
					</Table>
				</TableScroller>
			) : null}

			{requests.data && rows.length > 0 ? (
				<Pagination
					pagination={requests.data.pagination}
					page={page}
					onPageChange={setPage}
				/>
			) : null}
		</div>
	)
}
